import { useState } from 'react';
import type { Hito, PropuestaDetail } from '@/types';
import { useUIStore } from '@/store/uiStore';
import { hitosVisibles } from '@/lib/propuesta-selectors';
import { useEstadoChange } from '@/hooks/useEstadoChange';
import { ESTADO_META, EstadoSelect } from './EstadoSelect';
import { EstadoModal } from './EstadoModal';
import { EventosHistorial } from './EventosHistorial';
import styles from './facility-tabs.module.css';

interface Props {
  detail: PropuestaDetail;
}

const TIPO_BADGES = [
  { key: 'HF', label: 'Formal', flag: 'isFormal', color: '#2563eb', bg: '#dbeafe' },
  { key: 'HC', label: 'Clave', flag: 'isClave', color: '#059669', bg: '#d1fae5' },
] as const;

export function HitosView({ detail }: Props) {
  const lente = useUIStore((s) => s.lente);
  const scope = useUIStore((s) => s.scope);
  const visibles = hitosVisibles(detail, scope, lente);

  const propuestaId = detail.propuesta.id;
  const { change, pending, submit, cancel, isSubmitting } = useEstadoChange(propuestaId);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (visibles.length === 0) {
    return (
      <div className="placeholder-wrap">
        <div className="icon">🎯</div>
        <h3>Sin hitos en el subset actual</h3>
        <p>
          El lente <strong>{lente}</strong> no encontró hitos para este scope. Cambia el lente o
          selecciona otra EV/Iniciativa arriba.
        </p>
      </div>
    );
  }

  const groups: Record<string, Hito[]> = {
    propuesta: visibles.filter((h) => h.parentType === 'propuesta'),
    ev: visibles.filter((h) => h.parentType === 'ev'),
    iniciativa: visibles.filter((h) => h.parentType === 'iniciativa'),
    compromiso: visibles.filter((h) => h.parentType === 'compromiso'),
  };

  const groupLabels: Record<string, { title: string; icon: string }> = {
    propuesta: { title: 'Hitos globales de la Propuesta', icon: '🏛️' },
    ev: { title: 'Hitos de Entregas de Valor', icon: '🎯' },
    iniciativa: { title: 'Hitos de Iniciativas', icon: '💡' },
    compromiso: { title: 'Hitos de Compromisos', icon: '🛠️' },
  };

  const compIdToCompromiso = new Map(detail.compromisos.map((c) => [c.id, c]));
  const iniIdToIni = new Map(detail.iniciativas.map((i) => [i.id, i]));
  const evIdToEv = new Map(detail.entregasValor.map((e) => [e.id, e]));

  function getParentLabel(h: Hito): string {
    if (h.parentType === 'ev') {
      const ev = evIdToEv.get(h.parentId);
      return ev ? `EV · ${ev.nombre}` : '';
    }
    if (h.parentType === 'iniciativa') {
      const ini = iniIdToIni.get(h.parentId);
      return ini ? `Iniciativa · ${ini.nombre}` : '';
    }
    if (h.parentType === 'compromiso') {
      const c = compIdToCompromiso.get(h.parentId);
      if (!c) return '';
      const ini = iniIdToIni.get(c.iniciativaId);
      if (!ini) return `${c.codigo} · ${c.nombre}`;
      const ev = ini.entregaValorId ? evIdToEv.get(ini.entregaValorId) : null;
      const evPart = ev ? `${ev.nombre} › ` : '';
      return `${evPart}${ini.nombre} › ${c.codigo} · ${c.nombre}`;
    }
    return 'Propuesta';
  }

  // R4: suspendidos NO cuentan en el total
  const visiblesActivos = visibles.filter((h) => h.estado !== 'suspendido');
  const totales = visiblesActivos.length;
  const entregados = visiblesActivos.filter((h) => h.estado === 'entregado').length;
  const pct = totales ? Math.round((entregados / totales) * 100) : 0;

  return (
    <>
      <div className={styles.bpSummary}>
        <div>
          <div className={styles.bpLabel}>Hitos · Vista {lente}</div>
          <div className={styles.bpSub}>
            {scope.type
              ? `Filtrados por el scope activo en Row 1.`
              : `Sin scope: viendo todos los hitos de la propuesta bajo el lente actual.`}
          </div>
        </div>
        <div className={styles.bpRight}>
          <div className={styles.bpPct}>{pct}%</div>
          <div className={styles.bpFraction}>
            {entregados} de {totales} hitos entregados
          </div>
        </div>
      </div>

      {(['propuesta', 'ev', 'iniciativa', 'compromiso'] as const).map((pt) => {
        const hs = groups[pt];
        if (hs.length === 0) return null;
        // R4: suspendidos NO cuentan en el total por grupo
        const hsActivos = hs.filter((h) => h.estado !== 'suspendido');
        const entregadosG = hsActivos.filter((h) => h.estado === 'entregado').length;
        const totalG = hsActivos.length;
        const pctG = totalG ? Math.round((entregadosG / totalG) * 100) : 0;
        return (
          <div key={pt} className={styles.bpFase}>
            <div
              className={styles.bpFaseHeader}
              style={{
                background:
                  pt === 'propuesta'
                    ? '#0B3D62'
                    : pt === 'ev'
                      ? '#7c3aed'
                      : pt === 'iniciativa'
                        ? '#2563eb'
                        : '#d97706',
              }}
            >
              <div className={styles.bpFaseTitle}>
                <span style={{ fontSize: 20 }}>{groupLabels[pt].icon}</span>
                <span>{groupLabels[pt].title}</span>
              </div>
              <div className={styles.bpFaseProgress}>
                <span className={styles.bpFaseCount}>
                  {entregadosG} / {totalG}
                </span>
                <div className={styles.bpFaseBar}>
                  <div className={styles.bpFaseFill} style={{ width: `${pctG}%` }} />
                </div>
                <span className={styles.bpFasePct}>{pctG}%</span>
              </div>
            </div>

            <div>
              {hs.map((h) => {
                const parentLabel = getParentLabel(h);
                const meta = ESTADO_META[h.estado] ?? ESTADO_META.pendiente;
                const isDone = h.estado === 'entregado';
                const isBloqueado = h.estado === 'bloqueado';
                const isSuspendido = h.estado === 'suspendido';
                const isExpanded = expanded.has(h.id);

                return (
                  <div key={h.id}>
                    <div
                      className={[
                        styles.bpHitoCompact,
                        isDone ? styles.bpHitoCompactDone : '',
                        isBloqueado ? styles.bpHitoCompactBloqueado : '',
                        isSuspendido ? styles.bpHitoCompactSuspendido : '',
                      ].join(' ')}
                    >
                      <span
                        className={styles.bpDot}
                        style={{ background: meta.color }}
                        title={meta.label}
                      />

                      <div className={styles.bpCompactBody}>
                        <div className={styles.bpLine1}>
                          <div className={styles.bpLine1Left}>
                            <span className={styles.bpLine1Title}>{h.titulo}</span>
                            {parentLabel && (
                              <>
                                <span className={styles.bpLine1Sep}>·</span>
                                <span className={styles.bpLine1Parent}>{parentLabel}</span>
                              </>
                            )}
                          </div>

                          <div className={styles.estadoWrap}>
                            <EstadoSelect
                              value={h.estado}
                              disabled={isSubmitting}
                              onChange={(next) =>
                                change({
                                  hitoId: h.id,
                                  hitoTitulo: h.titulo,
                                  prevEstado: h.estado,
                                  nextEstado: next,
                                })
                              }
                            />
                            <button
                              type="button"
                              className={styles.historialBtn}
                              onClick={() => toggleExpand(h.id)}
                              aria-label="Ver historial"
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? '▾' : '▸'} historial
                            </button>
                          </div>
                        </div>

                        {(h.isFormal || h.isClave) && (
                          <div className={styles.bpLine2}>
                            {TIPO_BADGES.filter((b) => h[b.flag as keyof Hito] as boolean).map(
                              (b) => (
                                <span
                                  key={b.key}
                                  className={styles.bpBadge}
                                  style={{ background: b.bg, color: b.color }}
                                >
                                  {b.key} · {b.label}
                                </span>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {isExpanded && <EventosHistorial hitoId={h.id} />}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <EstadoModal
        open={!!pending}
        hitoTitulo={pending?.hitoTitulo ?? ''}
        estadoAnterior={pending?.prevEstado ?? null}
        estadoNuevo={pending?.nextEstado ?? null}
        onCancel={cancel}
        onSubmit={submit}
        submitting={isSubmitting}
      />
    </>
  );
}
